import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Delete, Edit, Visibility } from '@mui/icons-material';
import UserDetail from './UserDetails';
import UserEdit from './UserEdit';
import UserRegister from './UserRegister';
import api from '../services/api';
import { IUsuario } from '../interfaces';

const UserList: React.FC = () => {
    const [users, setUsers] = useState<IUsuario[]>([]);
    const [selectedUser, setSelectedUser] = useState<IUsuario | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<IUsuario | null>(null);

    // Carregar lista de usuários
    const fetchUsers = async () => {
        try {
            const response = await api.get('/api/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Erro ao carregar usuários:', error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Abrir Dialog de Detalhamento
    const handleOpenDetailDialog = (user: IUsuario) => {
        setSelectedUser(user);
        setDetailDialogOpen(true);
    };

    // Abrir Dialog de Edição
    const handleOpenEditDialog = (user: IUsuario) => {
        setSelectedUser(user);
        setEditDialogOpen(true);
    };

    // Abrir Dialog de Registro
    const handleOpenRegisterDialog = () => {
        setRegisterDialogOpen(true);
    };

    // Abrir Dialog de Confirmação de Deleção
    const handleOpenDeleteDialog = (user: IUsuario) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    // Confirmar deleção
    const handleDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await api.delete(`/api/users/${userToDelete._id}`);
            setDeleteDialogOpen(false);
            setUserToDelete(null);
            fetchUsers(); // Atualiza a lista de usuários
        } catch (error) {
            console.error('Erro ao deletar usuário:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                Lista de Usuários
            </Typography>
            <Button variant="contained" color="primary" onClick={handleOpenRegisterDialog}>
                Registrar Novo Usuário
            </Button>
            <Table sx={{ mt: 3 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>E-mail</TableCell>
                        <TableCell>Perfil</TableCell>
                        <TableCell>Ativo</TableCell>
                        <TableCell>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {users.map((user) => (
                        <TableRow key={user._id}>
                            <TableCell>{user.nome}</TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.perfil === 'Administrador' ? 'Administrador' : 'Usuário'}</TableCell>
                            <TableCell>{user.ativo ? 'Sim' : 'Não'}</TableCell>
                            <TableCell>
                                <Tooltip title="Detalhes">
                                    <IconButton onClick={() => handleOpenDetailDialog(user)}>
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Editar">
                                    <IconButton onClick={() => handleOpenEditDialog(user)}>
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Deletar">
                                    <IconButton onClick={() => handleOpenDeleteDialog(user)}>
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {/* Dialog de Detalhamento */}
            <UserDetail
                open={detailDialogOpen}
                onClose={() => setDetailDialogOpen(false)}
                user={selectedUser}
            />

            {/* Dialog de Edição */}
            <UserEdit
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                user={selectedUser}
                onSave={fetchUsers}
            />

            {/* Dialog de Registro */}
            <UserRegister
                open={registerDialogOpen}
                onClose={() => setRegisterDialogOpen(false)}
                onSave={fetchUsers}
            />

            {/* Dialog de Confirmação de Deleção */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirmar Deleção</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza de que deseja deletar o usuário{' '}
                        <strong>{userToDelete?.nome}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteUser} variant="contained" color="error">
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserList;